/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
import { useSitecore } from '@sitecore-content-sdk/nextjs';
import { withStandardComponentWrapper } from 'helpers/HOC';
import useDictionary from 'lib/hooks/useDictionary';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { useVideoWidth } from 'lib/hooks/useVideoWidth';
import { getVideoPlatform, getYouTubeEmbedUrl, getVimeoUrl } from 'lib/utils/videoUtils';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import TrackedVideoIframe from 'helpers/GenericWrappers/TrackedVideoIframe/TrackedVideoIframe';
import { getTestProps } from 'lib/testing/utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const DEFAULT_VIDEO_HEIGHT = 500;
/*
 * RENDERING
 */ const InlineVideo = (props)=>{
    const { height, width, url, title } = props?.fields || {};
    const { getDictionaryValue } = useDictionary();
    const { sitecoreContext } = useSitecoreContext();
    const { page } = useSitecore();
    const isEditing = page?.mode?.isEditing;
    const youTubeBaseURL = sitecoreContext?.siteSettings?.youTubeBaseURL?.value;
    const videoWidth = `${width?.value}` || 'auto';
    const videoHeight = `${height?.value}` || `${DEFAULT_VIDEO_HEIGHT}`;
    const { videoWrapper, videoIframeStyle } = TAILWIND_VARIANTS();
    const { containerRef, calculatedWidth, calculatedHeight, isFullWidth } = useVideoWidth({
        width: videoWidth,
        height: videoHeight
    });
    if (!url?.value) return /*#__PURE__*/ _jsx("div", {
        "data-component": "authorable/shared/media/inlinevideo",
        className: "video",
        ...editingHydrationProps(isEditing),
        children: /*#__PURE__*/ _jsx(EditingHelpText, {
            priority: "warning",
            children: getDictionaryValue('VideoUnavailable') || 'No video url is present'
        })
    });
    const videoUrl = getVideoPlatform(url?.value || '') === 'youtube' ? getYouTubeEmbedUrl(url?.value, youTubeBaseURL) : getVimeoUrl(url?.value);
    const widthInString = isFullWidth ? calculatedWidth : `${calculatedWidth}px`;
    const heightInString = isFullWidth ? calculatedHeight : `${calculatedHeight}px`;
    return (
        /*#__PURE__*/ _jsxs("section", {
            "data-component": "authorable/shared/media/inlinevideo",
            ...editingHydrationProps(isEditing),
            ref: containerRef,
            ...getTestProps(`component-inline-video-${props?.rendering?.uid}`),
            children: [
                (()=>{
                    const raw = {
                        '@context': 'https://schema.org',
                        '@type': 'VideoObject',
                        ...(title?.value ? {
                            name: title?.value
                        } : {}),
                        ...(url?.value ? {
                            contentUrl: url?.value
                        } : {}),
                        ...(videoUrl ? {
                            embedUrl: videoUrl
                        } : {})
                    };
                    const jsonLd = Object.fromEntries(Object.entries(raw).filter(([, value])=>value !== undefined));
                    if (isEditing) return null;
                    return /*#__PURE__*/ _jsx("script", {
                        type: "application/ld+json",
                        dangerouslySetInnerHTML: {
                            __html: JSON.stringify(jsonLd)
                        }
                    });
                })(),
                /*#__PURE__*/ _jsx("div", {
                    style: {
                        width: widthInString,
                        height: heightInString
                    },
                    className: videoWrapper(),
                    children: /*#__PURE__*/ _jsx(TrackedVideoIframe, {
                        videoUrl: url?.value || '',
                        videoTitle: title?.value?.trim() || 'Video content',
                        src: videoUrl,
                        width: calculatedWidth,
                        height: calculatedHeight,
                        title: title?.value?.trim() || 'Video content',
                        "aria-label": title?.value?.trim() || 'Video content',
                        className: videoIframeStyle({
                            isFullWidthVimeo: isFullWidth
                        }),
                        allowFullScreen: true,
                        style: {
                            aspectRatio: 16 / 9
                        },
                        allow: "autoplay; fullscreen; picture-in-picture; encrypted-media; presentation",
                        sandbox: "allow-same-origin allow-scripts allow-presentation",
                        referrerPolicy: "strict-origin-when-cross-origin",
                        ...getTestProps(`video`)
                    })
                })
            ]
        })
    );
};
const TAILWIND_VARIANTS = tv({
    slots: {
        videoWrapper: [
            'relative',
            'aspect-video',
            'mx-auto'
        ],
        videoIframeStyle: [
            'mx-auto'
        ]
    },
    variants: {
        isFullWidthVimeo: {
            true: {
                videoIframeStyle: [
                    'absolute',
                    'inset-0',
                    'w-full',
                    'h-full'
                ]
            }
        }
    }
});
export const Default = withStandardComponentWrapper(InlineVideo);
